// PrayerWatcherSaga.js
import { put, call, takeLatest } from "redux-saga/effects";
import * as api from "./PrayerAPI";
import * as actionTypes from "../ActionTypes";
import { actionUpdateGlobalLoaderSagaAction } from "../GlobalLoader/GlobalLoaderAction";

// Existing saga for creating prayer request
function* createPrayerRequestSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload || {};


    const response = yield call(api.createPrayerRequest, apiPayloadRequest);

    yield put({
      type: actionTypes.PRAYER_REQUEST_CREATE_SUCCESS,
      payload: response,
    });

    if (typeof callback === "function") {
      callback({
        type: 1,
        data: response?.data,
        message: response?.message,
      });
    }
  } catch (err) {
    const message =
      err?.response?.data?.errors?.[0]?.message ||
      err?.message ||
      "Prayer request failed";


    yield put({
      type: actionTypes.PRAYER_REQUEST_CREATE_FAIL,
      payload: message,
    });

    if (typeof action.payload?.callback === "function") {
      action.payload.callback({ type: 0, error: message });
    }
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}

// New saga for fetching user's prayer requests
function* fetchMyPrayerRequestsSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {

    const response = yield call(api.fetchMyPrayerRequests);


    // Transform the data if needed
    const prayerRequests = response.data || [];
    
    // Flatten the prayers array if needed
    const allPrayers = prayerRequests.reduce((acc, user) => {
      if (user.prayers && Array.isArray(user.prayers)) {
        return [
          ...acc,
          ...user.prayers.map(prayer => ({
            ...prayer,
            userId: user._id,
            userName: `${user.firstName} ${user.lastName}`,
            userProfilePic: user.profilePicUrl,
          }))
        ];
      }
      return acc;
    }, []);

    yield put({
      type: actionTypes.MY_PRAYER_REQUESTS_FETCH_SUCCESS,
      payload: allPrayers,
    });

    // Call callback if provided
    if (action.payload && typeof action.payload.callback === "function") {
      action.payload.callback({
        type: 1,
        data: allPrayers,
        message: response.message || "Prayer requests fetched successfully",
      });
    }

  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to fetch prayer requests";


    yield put({
      type: actionTypes.MY_PRAYER_REQUESTS_FETCH_FAIL,
      payload: message,
    });

    if (action.payload && typeof action.payload.callback === "function") {
      action.payload.callback({
        type: 0,
        error: message,
        data: [],
      });
    }
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}
function* createPrayerResponseSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload;
    const response = yield call(api.createPrayerResponse, apiPayloadRequest);

    yield put({
      type: actionTypes.PRAYER_RESPONSE_CREATE_SUCCESS,
      payload: response,
    });

    callback?.({ type: 1, data: response.data });
  } catch (err) {
    const message = err?.message || "Create response failed";

    yield put({
      type: actionTypes.PRAYER_RESPONSE_CREATE_FAIL,
      payload: message,
    });

    action.payload?.callback?.({ type: 0, error: message });
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}
function* updatePrayerResponseSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload;
    const response = yield call(api.updatePrayerResponse, apiPayloadRequest);

    yield put({
      type: actionTypes.PRAYER_RESPONSE_UPDATE_SUCCESS,
      payload: response,
    });

    callback?.({ type: 1, data: response.data });
  } catch (err) {
    const message = err?.message || "Update response failed";

    yield put({
      type: actionTypes.PRAYER_RESPONSE_UPDATE_FAIL,
      payload: message,
    });

    action.payload?.callback?.({ type: 0, error: message });
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}
function* deletePrayerResponseSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload;
    const response = yield call(api.deletePrayerResponse, apiPayloadRequest);

    yield put({
      type: actionTypes.PRAYER_RESPONSE_DELETE_SUCCESS,
      payload: response,
    });

    callback?.({ type: 1 });
  } catch (err) {
    const message = err?.message || "Delete response failed";

    yield put({
      type: actionTypes.PRAYER_RESPONSE_DELETE_FAIL,
      payload: message,
    });

    action.payload?.callback?.({ type: 0, error: message });
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}
function* readPrayerResponsesSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { apiPayloadRequest, callback } = action.payload;
    const response = yield call(api.readPrayerResponses, apiPayloadRequest);

    yield put({
      type: actionTypes.PRAYER_RESPONSE_READ_SUCCESS,
      payload: response.data || [],
    });

    callback?.({ type: 1, data: response.data });
  } catch (err) {
    const message = err?.message || "Read responses failed";

    yield put({
      type: actionTypes.PRAYER_RESPONSE_READ_FAIL,
      payload: message,
    });

    action.payload?.callback?.({ type: 0, error: message, data: [] });
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}
// PrayerWatcherSaga.js
function* fetchMyPrayersListSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { page = 1, limit = 10, callback } = action.payload || {};


    const response = yield call(api.fetchMyPrayersList, { page, limit });

    /**
     * Expected backend response example:
     * {
     *   data: [],
     *   pagination: { page, limit, total }
     * }
     */

    yield put({
      type: actionTypes.MY_PRAYERS_LIST_FETCH_SUCCESS,
      payload: {
        data: response.data || [],
        pagination: response.pagination || {
          page,
          limit,
          total: 0,
        },
      },
    });

    callback?.({
      type: 1,
      data: response.data,
      pagination: response.pagination,
      message: response.message || "Prayers fetched successfully",
    });
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to fetch prayers list";


    yield put({
      type: actionTypes.MY_PRAYERS_LIST_FETCH_FAIL,
      payload: message,
    });

    action.payload?.callback?.({
      type: 0,
      error: message,
      data: [],
    });
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}
function* fetchChurchFollowersPrayersSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const {
      churchObjId,
      page = 1,
      limit = 10,
      callback,
    } = action.payload || {};


    const response = yield call(api.fetchChurchFollowersPrayers, {
      churchObjId,
      page,
      limit,
    });

    /**
     * Expected response:
     * {
     *   data: [],
     *   pagination: { page, limit, total }
     * }
     */

    yield put({
      type: actionTypes.CHURCH_FOLLOWERS_PRAYERS_FETCH_SUCCESS,
      payload: {
        data: response.data || [],
        pagination: response.pagination || {
          page,
          limit,
          total: 0,
        },
      },
    });

    callback?.({
      type: 1,
      data: response.data,
      pagination: response.pagination,
      message: response.message || "Followers prayers fetched",
    });
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to fetch followers prayers";


    yield put({
      type: actionTypes.CHURCH_FOLLOWERS_PRAYERS_FETCH_FAIL,
      payload: message,
    });

    action.payload?.callback?.({
      type: 0,
      error: message,
      data: [],
    });
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}
function* fetchChannelFollowersPrayersSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { channelObjId, page = 1, limit = 10, callback } =
      action.payload || {};

    const response = yield call(api.fetchChannelFollowersPrayers, {
      channelObjId,
      page,
      limit,
    });

    yield put({
      type: actionTypes.CHANNEL_FOLLOWERS_PRAYERS_FETCH_SUCCESS,
      payload: {
        data: response.data || [],
        pagination: response.pagination || { page, limit, total: 0 },
      },
    });

    callback?.({
      type: 1,
      data: response.data,
      pagination: response.pagination,
    });
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to fetch channel followers prayers";

    yield put({
      type: actionTypes.CHANNEL_FOLLOWERS_PRAYERS_FETCH_FAIL,
      payload: message,
    });

    action.payload?.callback?.({ type: 0, error: message, data: [] });
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}
function* fetchMinistriesFollowersPrayersSaga(action) {
  yield put(actionUpdateGlobalLoaderSagaAction(true));

  try {
    const { ministriesObjId, page = 1, limit = 10, callback } =
      action.payload || {};

    const response = yield call(api.fetchMinistriesFollowersPrayers, {
      ministriesObjId,
      page,
      limit,
    });

    yield put({
      type: actionTypes.MINISTRIES_FOLLOWERS_PRAYERS_FETCH_SUCCESS,
      payload: {
        data: response.data || [],
        pagination: response.pagination || { page, limit, total: 0 },
      },
    });

    callback?.({
      type: 1,
      data: response.data,
      pagination: response.pagination,
    });
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to fetch ministries followers prayers";

    yield put({
      type: actionTypes.MINISTRIES_FOLLOWERS_PRAYERS_FETCH_FAIL,
      payload: message,
    });

    action.payload?.callback?.({ type: 0, error: message, data: [] });
  } finally {
    yield put(actionUpdateGlobalLoaderSagaAction(false));
  }
}


export default function* PrayerWatcherSaga() {
  yield takeLatest(
    actionTypes.PRAYER_REQUEST_CREATE_REQUEST,
    createPrayerRequestSaga
  );
  
  yield takeLatest(
    actionTypes.MY_PRAYER_REQUESTS_FETCH_REQUEST,
    fetchMyPrayerRequestsSaga
  );
  yield takeLatest(
    actionTypes.PRAYER_RESPONSE_CREATE_REQUEST,
    createPrayerResponseSaga
  );

  yield takeLatest(
    actionTypes.PRAYER_RESPONSE_UPDATE_REQUEST,
    updatePrayerResponseSaga
  );

  yield takeLatest(
    actionTypes.PRAYER_RESPONSE_DELETE_REQUEST,
    deletePrayerResponseSaga
  );

  yield takeLatest(
    actionTypes.PRAYER_RESPONSE_READ_REQUEST,
    readPrayerResponsesSaga
  );
  yield takeLatest(
    actionTypes.MY_PRAYERS_LIST_FETCH_REQUEST,
    fetchMyPrayersListSaga
  );
  yield takeLatest(
    actionTypes.CHURCH_FOLLOWERS_PRAYERS_FETCH_REQUEST,
    fetchChurchFollowersPrayersSaga
  );
  yield takeLatest(
    actionTypes.CHANNEL_FOLLOWERS_PRAYERS_FETCH_REQUEST,
    fetchChannelFollowersPrayersSaga
  );
  
  yield takeLatest(
    actionTypes.MINISTRIES_FOLLOWERS_PRAYERS_FETCH_REQUEST,
    fetchMinistriesFollowersPrayersSaga
  );
  
}